import { Notifications } from '@mantine/notifications'
import { useMediaQuery } from '@mantine/hooks'
import { useMantineTheme } from '@mantine/core'
import { useAuthLayout } from '~/features/layout/context'

/**
 * Global notification container with mobile-first positioning
 * 
 * Mobile: Bottom-center (thumb zone friendly)
 * Desktop: Bottom-right (traditional but accessible)
 * 
 * Features:
 * - Proper z-index above footer
 * - Responsive positioning
 * - Dynamic spacing based on footer height
 * - ARIA accessibility built-in via Mantine
 * - Prevents horizontal scroll on mobile
 */
export function NotificationContainer() {
  const theme = useMantineTheme()
  // SSR-safe: use false as initial value to match server render
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`, false, {
    getInitialValueInEffect: false,
  })
  const { footerHeight, showFooter } = useAuthLayout()

  // Calculate bottom offset: account for footer + safe spacing
  const bottomOffset = showFooter && isMobile 
    ? footerHeight + 16 // Footer height + 16px spacing
    : 24 // Standard spacing when no footer

  return (
    <Notifications
      position={isMobile ? 'bottom-center' : 'bottom-right'}
      zIndex={700} // Ensure above footer (footer is typically 200-300)
      limit={1}
      containerWidth={isMobile ? 'calc(100% - 32px)' : 400}
      styles={{
        root: {
          // Ensure notifications appear above footer
          position: 'fixed',
          bottom: `${bottomOffset}px`,
          left: isMobile ? '16px' : 'auto',
          right: isMobile ? '16px' : '24px',
          zIndex: 700,
          // Prevent horizontal scroll on mobile
          maxWidth: isMobile ? 'calc(100vw - 32px)' : '400px',
          // Ensure notifications don't cause page overflow
          pointerEvents: 'none',
          '& > *': {
            pointerEvents: 'auto',
          },
        },
        notification: {
          // Mobile-first styling
          marginBottom: theme.spacing.xs,
          borderRadius: theme.radius.md,
          boxShadow: theme.shadows.lg,
          // Ensure proper spacing from footer
          marginTop: 0,
          // Smooth animations
          transition: 'transform 0.2s ease, opacity 0.2s ease',
        },
      }}
    />
  )
}
